import type { ObservationRecord } from '@/lib/state/types';
import type { Instrument } from '@/lib/schema/types';
import { scoreInstrument, type TotalScoreResult } from '@/lib/scoring/totalScore';

/**
 * Snapshot export format.
 *
 * Wraps the raw observation with the computed scores at export-time so the
 * file is self-describing: consumers (a dashboard, a QA reviewer, a Python
 * script) don't have to re-run the scoring engine to read it, and the
 * `schemaVersion` + `exportedAt` make it easy to spot drift if the rubric
 * ever bumps.
 *
 * The record fields are copied verbatim — re-importing via the Landing
 * screen round-trips losslessly.
 */
export interface ObservationExport {
  schemaVersion: string;
  exportedAt: string; // ISO8601
  record: ObservationRecord;
  scores: {
    total: {
      average: number | null;
      scoredItems: number;
      naItems: number;
      pendingItems: number;
    };
    subscales: Array<{
      id: string;
      name: string;
      average: number | null;
      scoredItems: number;
      naItems: number;
      pendingItems: number;
      items: Array<{
        id: string;
        number: number;
        name: string;
        score: number | null;
        itemNa: boolean;
        complete: boolean;
      }>;
    }>;
  };
}

export function buildObservationExport(
  instrument: Instrument,
  record: ObservationRecord,
): ObservationExport {
  const total: TotalScoreResult = scoreInstrument(
    instrument,
    record.responses,
    record.itemNa,
  );
  return {
    schemaVersion: record.schemaVersion,
    exportedAt: new Date().toISOString(),
    record,
    scores: {
      total: {
        average: total.average,
        scoredItems: total.scoredItems,
        naItems: total.naItems,
        pendingItems: total.pendingItems,
      },
      subscales: total.subscales.map((s) => ({
        id: s.id,
        name: s.name,
        average: s.result.average,
        scoredItems: s.result.scoredItems,
        naItems: s.result.naItems,
        pendingItems: s.result.pendingItems,
        items: s.result.itemResults.map((r) => ({
          id: r.item.id,
          number: r.item.number,
          name: r.item.name,
          score: r.score,
          itemNa: r.itemNa,
          complete: r.complete,
        })),
      })),
    },
  };
}

export function downloadObservationJson(
  instrument: Instrument,
  record: ObservationRecord,
): void {
  const payload = buildObservationExport(instrument, record);
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const site = (record.metadata.siteName || 'observation')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  a.download = `fccers-${site}-${record.metadata.observationDate || 'draft'}.json`;
  a.click();
  URL.revokeObjectURL(url);
}
